/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import ClassNames from "Helpers/Common";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { useVisitTypeVersions } from "CustomHooks/useVisitTypes";
import { QUESTION_TYPES, CHECKOUT_FEATURES, conditionValueToText } from "./constants";
import { ReactComponent as ArrowDownIcon } from "Assets/ArrowDown.svg";

const typeLabel = (type) => QUESTION_TYPES.find((entry) => entry.value === type)?.label || type;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return (
    date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " · " +
    date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
};

const Chip = ({ className, children }) => (
  <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${className}`}>{children}</span>
);

// Features with a custom price item mapped (null sku = global default pricing)
const customPricedFeatures = (snapshot) => CHECKOUT_FEATURES.filter((feature) => snapshot?.features?.[feature.key]?.sku);

const resolvedPrice = (snapshot, slotKey) => {
  const resolved = snapshot?.resolved;
  if (!resolved) return null;
  const slot = slotKey === "base" ? resolved.base : resolved.features?.[slotKey];
  return slot?.price != null ? `$${slot.price}` : null;
};

export const VersionsTab = ({ visitTypeId, publishedVersionId }) => {
  const [expandedIds, setExpandedIds] = useState([]);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const { data, isLoading, error } = useVisitTypeVersions(visitTypeId);
  const versions = data?.data || [];

  const toggleExpanded = (versionId) =>
    setExpandedIds((current) => (current.includes(versionId) ? current.filter((entry) => entry !== versionId) : [...current, versionId]));

  useEffect(() => {
    setErrorDismissed(false);
    if (versions.length) setExpandedIds([versions[0].id]);
  }, [visitTypeId, data]);

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-3">
      {error && !errorDismissed && (
        <ApiError error={errorFormatter(error?.response?.data?.errors || error?.message)} setApiError={() => setErrorDismissed(true)} />
      )}
      {versions.length === 0 && <p className="text-sm text-gray-500">Nothing published yet.</p>}
      {versions.map((version, index) => {
        const isLive = publishedVersionId && version.id === publishedVersionId;
        const expanded = expandedIds.includes(version.id);
        const previous = versions[index + 1];
        const questionDelta = previous != null ? (version.questions?.length ?? 0) - (previous.questions?.length ?? 0) : null;
        return (
          <div
            key={version.id}
            className={ClassNames("bg-white border rounded-lg", isLive ? "border-green-300 ring-1 ring-green-200" : "border-gray-200")}
          >
            <button
              type="button"
              className="w-full flex items-center justify-between gap-4 p-4 text-left"
              onClick={() => toggleExpanded(version.id)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={ClassNames(
                    "px-2.5 py-1 text-sm font-semibold rounded-md",
                    isLive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600",
                  )}
                >
                  v{version.version}
                </span>
                {isLive && <Chip className="bg-green-600 text-white">LIVE</Chip>}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {version.questions?.length ?? 0} questions
                    {questionDelta !== null && questionDelta !== 0 && (
                      <span className={ClassNames("ml-2 text-xs", questionDelta > 0 ? "text-green-600" : "text-red-500")}>
                        ({questionDelta > 0 ? `+${questionDelta}` : questionDelta} vs v{previous.version})
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {version.checkoutSnapshot?.baseSku && (
                      <>
                        base <span className="font-mono">{version.checkoutSnapshot.baseSku}</span>
                        {resolvedPrice(version.checkoutSnapshot, "base") && ` (${resolvedPrice(version.checkoutSnapshot, "base")})`}
                        {" · "}
                      </>
                    )}
                    {customPricedFeatures(version.checkoutSnapshot).length
                      ? `custom price: ${customPricedFeatures(version.checkoutSnapshot)
                          .map((feature) => feature.label)
                          .join(", ")}`
                      : "default add-on pricing"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-right">
                  <div className="text-sm text-gray-700">{version.publishedBy === "seed" ? "System seed" : version.publishedBy}</div>
                  <div className="text-xs text-gray-500">{formatDate(version.publishedAt)}</div>
                </div>
                <ArrowDownIcon className={ClassNames("h-5 w-5 text-gray-400 transform transition-transform", expanded && "rotate-180")} />
              </div>
            </button>

            {expanded && (
              <div className="border-t border-gray-100 p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Questions</h4>
                  {(version.questions || []).length === 0 && (
                    <p className="text-sm text-gray-500">No questions — form goes straight to checkout.</p>
                  )}
                  <ol className="space-y-2">
                    {(version.questions || []).map((question, questionIndex) => (
                      <li key={question.key} className="flex items-start gap-2">
                        <span className="text-xs text-gray-400 font-mono pt-0.5 w-5 text-right flex-shrink-0">{questionIndex + 1}.</span>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-900">{question.title}</div>
                          <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <Chip className="bg-blue-50 text-blue-700">{typeLabel(question.type)}</Chip>
                            <Chip className="bg-gray-100 text-gray-500 font-mono">{question.key}</Chip>
                            {question.showIf?.length > 0 && (
                              <Chip className="bg-purple-50 text-purple-700">
                                if{" "}
                                {question.showIf
                                  .map((condition) => `${condition.questionKey} ${condition.op} ${conditionValueToText(condition.value)}`)
                                  .join(" AND ")}
                              </Chip>
                            )}
                            {question.disqualify && (
                              <Chip
                                className={
                                  question.disqualify.action === "BLOCK" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"
                                }
                              >
                                {question.disqualify.action}
                              </Chip>
                            )}
                            {question.options?.some((option) => option.disqualify) && (
                              <Chip className="bg-yellow-50 text-yellow-700">option disqualifiers</Chip>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Checkout at publish</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base</span>
                      <span className="font-mono text-gray-900">
                        {version.checkoutSnapshot?.baseSku || "—"}
                        {resolvedPrice(version.checkoutSnapshot, "base") && ` · ${resolvedPrice(version.checkoutSnapshot, "base")}`}
                      </span>
                    </div>
                    {CHECKOUT_FEATURES.map((feature) => {
                      const state = version.checkoutSnapshot?.features?.[feature.key];
                      return (
                        <div key={feature.key} className="flex justify-between">
                          <span className="text-gray-600">{feature.label}</span>
                          {state?.enabled === false ? (
                            // pre-2026-07-23 snapshots could disable features
                            <span className="text-gray-400">off (legacy)</span>
                          ) : (
                            <span className="font-mono text-gray-900">
                              {state?.sku || "default"}
                              {resolvedPrice(version.checkoutSnapshot, feature.key) &&
                                ` · ${resolvedPrice(version.checkoutSnapshot, feature.key)}`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
